// In-memory virtual filesystem used by the browser-side verifier.
//
// The verifier in src/lib/verify.ts reads files through node:fs/promises.
// The browser has no filesystem, so we route those reads to this Map,
// which is populated by the receipt-input adapters at the moment the
// user supplies a receipt package.
//
// A path is matched in three steps:
//   1. The literal path against the in-memory virtual FS (receipt files).
//   2. A "registry/", "schemas/", or "profiles/" prefix served from the
//      bundled static assets at the same path on the deployed origin.
//   3. Otherwise, ENOENT.
//
// This shape makes it trivial to add additional input adapters (URL
// fetch, hosted storage) later without changing the verification path.

import path from "path-browserify";

const files = new Map<string, string>();
const directories = new Map<string, Set<string>>();

function normalize(p: string): string {
  if (!p) return p;
  const normalized = path.normalize(p);
  return normalized.replace(/^\.\//, "");
}

export function registerFile(filePath: string, contents: string): void {
  const p = normalize(filePath);
  files.set(p, contents);
  const dir = path.dirname(p);
  if (!directories.has(dir)) directories.set(dir, new Set());
  directories.get(dir)!.add(path.basename(p));
}

export function clearVirtualFs(): void {
  files.clear();
  directories.clear();
}

export function hasFile(p: string): boolean {
  return files.has(normalize(p));
}

export function getFile(p: string): string | undefined {
  return files.get(normalize(p));
}

export function listDirectory(dirPath: string): string[] | undefined {
  const entries = directories.get(normalize(dirPath));
  if (!entries) return undefined;
  return Array.from(entries).sort();
}

export function listAllFiles(): string[] {
  return Array.from(files.keys()).sort();
}

const REGISTRY_PREFIXES = ["registry/", "schemas/", "profiles/"];

function isRegistryPath(p: string): boolean {
  const normalized = normalize(p);
  return REGISTRY_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export async function fetchRegistryFile(p: string): Promise<string | undefined> {
  if (!isRegistryPath(p)) return undefined;
  const url = `/${normalize(p)}`;
  try {
    const response = await fetch(url);
    if (!response.ok) return undefined;
    return await response.text();
  } catch {
    return undefined;
  }
}
